import React, { useEffect, useMemo, useState } from 'react';
import { Canvas } from '@react-three/fiber';
import { OrbitControls } from '@react-three/drei/core/OrbitControls';
import * as THREE from 'three';

// A simple component to render the concept graph.
function Graph({ nodes, edges, selectedId, onSelect }) {
  // Precompute random positions for the nodes. In a real app you would
  // compute positions based on the concept embeddings or with a layout algorithm.
  const positions = useMemo(() => {
    const map = {};
    nodes.forEach((node) => {
      map[node.id] = new THREE.Vector3(
        Math.random() * 4 - 2,
        Math.random() * 4 - 2,
        Math.random() * 4 - 2
      );
    });
    return map;
  }, [nodes]);

  const [hoveredId, setHoveredId] = useState(null);

  return (
    <>
      {nodes.map((node) => {
        const pos = positions[node.id];
        const color = node.id === selectedId ? '#f97316' : hoveredId === node.id ? '#60a5fa' : 'skyblue';
        const scale = node.id === selectedId ? 1.4 : hoveredId === node.id ? 1.2 : 1;
        return (
          <mesh
            key={node.id}
            position={pos}
            scale={scale}
            onClick={(event) => {
              event.stopPropagation();
              onSelect(node.id);
            }}
            onPointerOver={(event) => {
              event.stopPropagation();
              setHoveredId(node.id);
            }}
            onPointerOut={() => {
              setHoveredId((current) => (current === node.id ? null : current));
            }}
          >
            <sphereGeometry args={[0.1, 16, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
        );
      })}
      {/* Edges represented as lines. */}
      {edges.map((edge, idx) => {
        const start = positions[edge.source];
        const end = positions[edge.target];
        const points = [start, end];
        const geometry = new THREE.BufferGeometry().setFromPoints(points);
        return (
          <line key={idx} geometry={geometry}>
            <lineBasicMaterial color="lightgray" />
          </line>
        );
      })}
      <ambientLight intensity={0.4} />
      <pointLight position={[5, 5, 5]} intensity={1.0} />
    </>
  );
}

export default function App() {
  const [graphData, setGraphData] = useState(null);
  const [turns, setTurns] = useState([]);
  const [selectedConcept, setSelectedConcept] = useState(null);

  // Load concept data on mount.
  useEffect(() => {
    const dataUrl = `${process.env.PUBLIC_URL || ''}/data/concepts.json`;
    const turnsUrl = `${process.env.PUBLIC_URL || ''}/data/socratic_turns.json`;

    Promise.all([
      fetch(dataUrl).then((res) => res.json()),
      fetch(turnsUrl).then((res) => res.json()),
    ])
      .then(([graph, turnsJson]) => {
        setGraphData(graph);
        setTurns(turnsJson);
        if (graph?.nodes?.length) {
          setSelectedConcept(graph.nodes[0].id);
        }
      })
      .catch((err) => console.error(err));
  }, []);

  const concept = useMemo(() => {
    if (!graphData || !selectedConcept) return null;
    return graphData.nodes.find((node) => node.id === selectedConcept) || null;
  }, [graphData, selectedConcept]);

  const conceptTurns = useMemo(() => {
    if (!selectedConcept) return [];
    return turns.filter((turn) => turn.concept_id === selectedConcept);
  }, [turns, selectedConcept]);

  if (!graphData) {
    return <div style={{ padding: '1rem' }}>Loading concept graph…</div>;
  }

  return (
    <div style={{ display: 'flex', height: '100vh', width: '100vw' }}>
      <div style={{ flex: '1 1 auto', position: 'relative' }}>
        <Canvas style={{ width: '100%', height: '100%' }} camera={{ position: [0, 0, 5] }}>
          <Graph
            nodes={graphData.nodes}
            edges={graphData.edges}
            selectedId={selectedConcept}
            onSelect={setSelectedConcept}
          />
          <OrbitControls />
        </Canvas>
      </div>
      <aside
        style={{
          width: '360px',
          maxWidth: '40vw',
          padding: '1.5rem',
          borderLeft: '1px solid #e5e7eb',
          backgroundColor: '#f9fafb',
          overflowY: 'auto',
        }}
      >
        {concept ? (
          <div>
            <h2 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem' }}>{concept.title}</h2>
            <p style={{ margin: '0 0 0.75rem', color: '#4b5563' }}>{concept.chapter}</p>
            <section style={{ marginBottom: '1.25rem' }}>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Prerequisites</h3>
              {concept.prerequisites && concept.prerequisites.length ? (
                <ul style={{ paddingLeft: '1.25rem', margin: 0 }}>
                  {concept.prerequisites.map((prereq) => (
                    <li key={prereq}>{prereq}</li>
                  ))}
                </ul>
              ) : (
                <p style={{ margin: 0 }}>None listed.</p>
              )}
            </section>

            <section>
              <h3 style={{ fontSize: '1rem', marginBottom: '0.5rem' }}>Socratic Prompts</h3>
              {conceptTurns.length ? (
                conceptTurns.map((turn) => (
                  <article
                    key={turn.turn_id}
                    style={{
                      padding: '0.75rem',
                      borderRadius: '0.5rem',
                      backgroundColor: '#fff',
                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.08)',
                      marginBottom: '0.75rem',
                    }}
                  >
                    <h4 style={{ margin: '0 0 0.35rem', fontSize: '0.95rem' }}>{turn.prompt}</h4>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong style={{ display: 'block', fontSize: '0.8rem', color: '#64748b' }}>
                        Expected Points
                      </strong>
                      <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0 0' }}>
                        {turn.expected_points.map((point, index) => (
                          <li key={index}>{point}</li>
                        ))}
                      </ul>
                    </div>
                    <div style={{ marginBottom: '0.5rem' }}>
                      <strong style={{ display: 'block', fontSize: '0.8rem', color: '#64748b' }}>
                        Feedback Paths
                      </strong>
                      <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0 0' }}>
                        <li>
                          <em>Correct:</em> {turn.feedback.correct}
                        </li>
                        <li>
                          <em>Partial:</em> {turn.feedback.partial}
                        </li>
                        <li>
                          <em>Incorrect:</em> {turn.feedback.incorrect}
                        </li>
                      </ul>
                    </div>
                    {turn.misconceptions?.length ? (
                      <div>
                        <strong style={{ display: 'block', fontSize: '0.8rem', color: '#64748b' }}>
                          Misconception Checks
                        </strong>
                        <ul style={{ paddingLeft: '1.25rem', margin: '0.25rem 0 0' }}>
                          {turn.misconceptions.map((misconception) => (
                            <li key={misconception.pattern}>
                              <em>{misconception.pattern}:</em> {misconception.feedback}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ) : null}
                  </article>
                ))
              ) : (
                <p style={{ margin: 0 }}>No Socratic prompts linked to this concept yet.</p>
              )}
            </section>
          </div>
        ) : (
          <p>Select a concept node to view its tutoring prompts.</p>
        )}
      </aside>
    </div>
  );
}
